import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { AlertTriangle, Calculator, Info, Loader2, Percent, Save } from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/errors';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Radix Select n'accepte pas la valeur vide : sentinelles
const AUTO = 'auto';
const NONE = 'none';

const MOTEUR_LABELS = {
  auto: 'Automatique : URSSAF mon-entreprise puis barème interne',
  urssaf: 'URSSAF mon-entreprise (repli barème si indisponible)',
  bareme: 'Barème interne',
  forfait: 'Forfait (taux fixes)',
};

const METHODE_BADGE = {
  urssaf_api: { label: 'URSSAF mon-entreprise', className: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200' },
  bareme_interne: { label: 'Barème interne', className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200' },
  forfait: { label: 'Forfait', className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200' },
};

const fmtEur = (v) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(Number(v) || 0);
const fmtPct = (v, brut) => (brut ? `${((Number(v) || 0) / brut * 100).toFixed(1).replace('.', ',')} %` : '—');

const numOrNull = (v) => (v === '' || v === null || v === undefined ? null : Number(String(v).replace(',', '.')));
const str = (v) => (v === null || v === undefined ? '' : String(v));

/**
 * Administration › Charges sociales : paramètres de l'estimation des cotisations (moteur, barème,
 * taux AT, effectif, convention collective, prévoyance / santé, taux forfaitaires) et simulateur.
 * GET/PUT /api/admin/charges-config, GET /api/admin/charges-config/baremes, POST /api/admin/charges-config/simuler
 */
const ChargesConfig = () => {
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState(null);
  const [baremes, setBaremes] = useState({ annees: [], conventions: [], moteurs: [], familles: {} });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const [sim, setSim] = useState({ brut: '2500', statut: 'non_cadre', moteur: AUTO });
  const [simLoading, setSimLoading] = useState(false);
  const [simResult, setSimResult] = useState(null);

  const toForm = useCallback((cfg) => ({
    moteur: cfg.moteur || 'auto',
    annee_bareme: cfg.annee_bareme ? String(cfg.annee_bareme) : AUTO,
    taux_at: str(cfg.taux_at ?? 2),
    effectif_override: str(cfg.effectif_override),
    idcc: cfg.idcc ? String(cfg.idcc) : NONE,
    versement_mobilite: str(cfg.versement_mobilite ?? 0),
    reduction_generale: cfg.reduction_generale !== false,
    prevoyance_cadre_pct: str(cfg.prevoyance_cadre_pct),
    prevoyance_non_cadre_pct: str(cfg.prevoyance_non_cadre_pct),
    sante_employeur_mensuel: str(cfg.sante_employeur_mensuel),
    taux_forfait_patronal: str(cfg.taux_forfait_patronal ?? 42),
    taux_forfait_salarial: str(cfg.taux_forfait_salarial ?? 22),
  }), []);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [cfgRes, barRes] = await Promise.all([
          axios.get(`${API_URL}/admin/charges-config`),
          axios.get(`${API_URL}/admin/charges-config/baremes`),
        ]);
        const cfg = cfgRes.data?.config || {};
        setConfig(cfg);
        setForm(toForm(cfg));
        setBaremes({
          annees: barRes.data?.annees || [],
          conventions: barRes.data?.conventions || [],
          moteurs: barRes.data?.moteurs || [],
          familles: barRes.data?.familles || {},
        });
      } catch (error) {
        toast.error(getErrorMessage(error, 'Chargement de la configuration des charges impossible'));
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [toForm]);

  const update = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setDirty(true);
  };

  const buildPayload = () => ({
    moteur: form.moteur,
    annee_bareme: form.annee_bareme === AUTO ? null : Number(form.annee_bareme),
    taux_at: numOrNull(form.taux_at) ?? 2,
    effectif_override: numOrNull(form.effectif_override),
    idcc: form.idcc === NONE ? null : form.idcc,
    versement_mobilite: numOrNull(form.versement_mobilite) ?? 0,
    reduction_generale: !!form.reduction_generale,
    prevoyance_cadre_pct: numOrNull(form.prevoyance_cadre_pct),
    prevoyance_non_cadre_pct: numOrNull(form.prevoyance_non_cadre_pct),
    sante_employeur_mensuel: numOrNull(form.sante_employeur_mensuel),
    taux_forfait_patronal: numOrNull(form.taux_forfait_patronal) ?? 42,
    taux_forfait_salarial: numOrNull(form.taux_forfait_salarial) ?? 22,
  });

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API_URL}/admin/charges-config`, buildPayload());
      const cfg = res.data?.config || {};
      setConfig(cfg);
      setForm(toForm(cfg));
      setDirty(false);
      toast.success('Configuration des charges sociales enregistrée');
    } catch (error) {
      toast.error(getErrorMessage(error, "Enregistrement impossible"));
    } finally {
      setSaving(false);
    }
  };

  const simuler = async () => {
    const brut = numOrNull(sim.brut);
    if (!brut || brut <= 0) { toast.error('Indiquez un salaire brut mensuel'); return; }
    if (dirty) toast.info('La simulation utilise la configuration enregistrée : pensez à enregistrer vos modifications');
    setSimLoading(true);
    try {
      const body = { brut, statut: sim.statut };
      if (sim.moteur !== AUTO) body.moteur = sim.moteur;
      const res = await axios.post(`${API_URL}/admin/charges-config/simuler`, body);
      setSimResult(res.data);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Simulation impossible'));
    } finally {
      setSimLoading(false);
    }
  };

  const anneeSel = useMemo(() => baremes.annees.find((a) => String(a.annee) === form?.annee_bareme), [baremes.annees, form]);
  const conventionSel = useMemo(() => baremes.conventions.find((c) => c.idcc && String(c.idcc) === form?.idcc), [baremes.conventions, form]);

  if (loading || !form) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Chargement…
      </div>
    );
  }

  const isForfait = form.moteur === 'forfait';
  const badge = simResult ? (METHODE_BADGE[simResult.methode] || { label: simResult.methode, className: '' }) : null;
  const familles = simResult ? Object.entries(simResult.par_famille || {}) : [];
  const libelles = simResult?.familles_libelles || baremes.familles || {};

  return (
    <div className="space-y-6" data-testid="charges-config">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Percent className="h-5 w-5" /> Estimation des charges sociales</CardTitle>
          <CardDescription>
            Utilisée par le reporting quand aucun logiciel de paie ne fournit les cotisations réelles (ou quand un bulletin n'a pas de
            montants patronaux). Chaque estimation conserve la méthode, la version du barème et les hypothèses.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2 md:col-span-2">
              <Label>Moteur d'estimation</Label>
              <Select value={form.moteur} onValueChange={(v) => update('moteur', v)}>
                <SelectTrigger data-testid="charges-moteur"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(baremes.moteurs.length ? baremes.moteurs : Object.entries(MOTEUR_LABELS).map(([code, label]) => ({ code, label }))).map((m) => (
                    <SelectItem key={m.code} value={m.code}>{MOTEUR_LABELS[m.code] || m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground flex items-start gap-1">
                <Info className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <span>
                  L'API publique du simulateur URSSAF mon-entreprise ne reçoit que le brut, le statut et l'effectif (aucune donnée nominative).
                  En cas d'indisponibilité, le barème interne prend le relais ; le forfait reste le dernier recours.
                </span>
              </p>
            </div>

            <div className="space-y-2">
              <Label>Année de barème</Label>
              <Select value={form.annee_bareme} onValueChange={(v) => update('annee_bareme', v)} disabled={isForfait}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={AUTO}>Automatique (année de la période)</SelectItem>
                  {baremes.annees.map((a) => (
                    <SelectItem key={a.annee} value={String(a.annee)}>
                      {a.annee} — v{a.version.split('.')[1] || '1'}{a.provisoire ? ' (provisoire)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {anneeSel && (
                <p className="text-xs text-muted-foreground">
                  PMSS {fmtEur(anneeSel.pmss)} · SMIC mensuel {fmtEur(anneeSel.smic_mensuel)}
                  {anneeSel.provisoire && <span className="ml-1 text-amber-600">· paramètres provisoires</span>}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Convention collective (IDCC)</Label>
              <Select value={form.idcc} onValueChange={(v) => update('idcc', v)} disabled={isForfait}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>Taux par défaut (aucune convention spécifique)</SelectItem>
                  {baremes.conventions.filter((c) => c.idcc).map((c) => (
                    <SelectItem key={c.idcc} value={String(c.idcc)}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!conventionSel && form.idcc === NONE && (
                <p className="text-xs text-muted-foreground">Prévoyance cadres au minimum conventionnel (1,50 % T1), aucune prévoyance non cadre.</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="charges-taux-at">Taux AT/MP de l'établissement (%)</Label>
              <Input id="charges-taux-at" type="number" step="0.01" min="0" max="30" value={form.taux_at}
                onChange={(e) => update('taux_at', e.target.value)} disabled={isForfait} />
              <p className="text-xs text-muted-foreground">Taux notifié par la CARSAT (défaut 2,00 %).</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="charges-effectif">Effectif de l'entreprise</Label>
              <Input id="charges-effectif" type="number" min="0" step="1" value={form.effectif_override}
                placeholder="10 par défaut" onChange={(e) => update('effectif_override', e.target.value)} disabled={isForfait} />
              <p className="text-xs text-muted-foreground">Seuils 11 (formation) et 50 (FNAL, réduction générale).</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="charges-vm">Versement mobilité (%)</Label>
              <Input id="charges-vm" type="number" step="0.01" min="0" max="10" value={form.versement_mobilite}
                onChange={(e) => update('versement_mobilite', e.target.value)} disabled={isForfait} />
              <p className="text-xs text-muted-foreground">Taux de votre autorité organisatrice (0 si non assujetti, effectif &lt; 11).</p>
            </div>

            <div className="flex items-center justify-between rounded-md border p-3">
              <div>
                <Label htmlFor="charges-rg">Réduction générale des cotisations patronales</Label>
                <p className="text-xs text-muted-foreground">Appliquée aux rémunérations inférieures à 1,6 SMIC (approximation).</p>
              </div>
              <Switch id="charges-rg" checked={!!form.reduction_generale} onCheckedChange={(v) => update('reduction_generale', v)} disabled={isForfait} />
            </div>
          </div>

          <div className="space-y-3">
            <h4 className="text-sm font-semibold">Prévoyance et santé (surcharges facultatives)</h4>
            <p className="text-xs text-muted-foreground">Laissez vide pour appliquer les taux de la convention collective sélectionnée.</p>
            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="charges-prev-cadre">Prévoyance cadres — part employeur (% du brut)</Label>
                <Input id="charges-prev-cadre" type="number" step="0.01" min="0" max="20" value={form.prevoyance_cadre_pct}
                  placeholder="Convention" onChange={(e) => update('prevoyance_cadre_pct', e.target.value)} disabled={isForfait} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="charges-prev-nc">Prévoyance non cadres — part employeur (% du brut)</Label>
                <Input id="charges-prev-nc" type="number" step="0.01" min="0" max="20" value={form.prevoyance_non_cadre_pct}
                  placeholder="Convention" onChange={(e) => update('prevoyance_non_cadre_pct', e.target.value)} disabled={isForfait} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="charges-sante">Complémentaire santé — part employeur (€/mois)</Label>
                <Input id="charges-sante" type="number" step="0.01" min="0" max="1000" value={form.sante_employeur_mensuel}
                  placeholder="Convention" onChange={(e) => update('sante_employeur_mensuel', e.target.value)} disabled={isForfait} />
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <h4 className="text-sm font-semibold">Taux forfaitaires (dernier recours)</h4>
            <p className="text-xs text-muted-foreground">
              Utilisés si le moteur « Forfait » est choisi ou si aucune estimation n'est possible. Ils remplacent les anciens taux
              « charges estimées » de la configuration paie.
            </p>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="charges-forfait-pat">Charges patronales (%)</Label>
                <Input id="charges-forfait-pat" type="number" step="0.1" min="0" max="100" value={form.taux_forfait_patronal}
                  onChange={(e) => update('taux_forfait_patronal', e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="charges-forfait-sal">Charges salariales (%)</Label>
                <Input id="charges-forfait-sal" type="number" step="0.1" min="0" max="100" value={form.taux_forfait_salarial}
                  onChange={(e) => update('taux_forfait_salarial', e.target.value)} />
              </div>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3">
            {dirty && <span className="text-xs text-amber-600">Modifications non enregistrées</span>}
            <Button onClick={save} disabled={saving || !dirty} data-testid="charges-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />} Enregistrer
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Calculator className="h-5 w-5" /> Simuler</CardTitle>
          <CardDescription>Testez la configuration enregistrée sur un salaire brut mensuel.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-4 items-end">
            <div className="space-y-2">
              <Label htmlFor="sim-brut">Brut mensuel (€)</Label>
              <Input id="sim-brut" type="number" min="1" step="1" value={sim.brut} onChange={(e) => setSim({ ...sim, brut: e.target.value })} data-testid="sim-brut" />
            </div>
            <div className="space-y-2">
              <Label>Statut</Label>
              <Select value={sim.statut} onValueChange={(v) => setSim({ ...sim, statut: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="non_cadre">Non cadre</SelectItem>
                  <SelectItem value="cadre">Cadre</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Moteur</Label>
              <Select value={sim.moteur} onValueChange={(v) => setSim({ ...sim, moteur: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={AUTO}>Selon la configuration ({MOTEUR_LABELS[config?.moteur] ? config.moteur : 'auto'})</SelectItem>
                  <SelectItem value="urssaf">URSSAF mon-entreprise</SelectItem>
                  <SelectItem value="bareme">Barème interne</SelectItem>
                  <SelectItem value="forfait">Forfait</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={simuler} disabled={simLoading} data-testid="sim-run">
              {simLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Calculator className="h-4 w-4 mr-2" />} Simuler
            </Button>
          </div>

          {simResult && (
            <div className="space-y-4" data-testid="sim-result">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="text-muted-foreground">Méthode :</span>
                <Badge variant="outline" className={badge.className}>{simResult.methode_libelle || badge.label}</Badge>
                {simResult.version_bareme && simResult.methode === 'bareme_interne' && (
                  <span className="text-xs text-muted-foreground">barème {simResult.version_bareme}{simResult.provisoire ? ' (provisoire)' : ''}</span>
                )}
                {simResult.cache && <span className="text-xs text-muted-foreground">(résultat en cache)</span>}
              </div>
              {simResult.fallback_raison && (
                <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-900/20 dark:text-amber-100 dark:border-amber-700">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span><strong>Repli :</strong> {simResult.fallback_raison}</span>
                </div>
              )}

              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
                <Stat label="Brut" value={fmtEur(simResult.brut)} />
                <Stat label="Cotisations salariales" value={fmtEur(simResult.cotisations_salariales)} hint={fmtPct(simResult.cotisations_salariales, simResult.brut)} />
                <Stat label="Net estimé (avant impôt)" value={fmtEur(simResult.net_estime)} />
                <Stat label="Cotisations patronales" value={fmtEur(simResult.cotisations_patronales)} hint={fmtPct(simResult.cotisations_patronales, simResult.brut)} />
                <Stat label="Coût employeur" value={fmtEur(simResult.cout_employeur)} strong />
              </div>
              {simResult.reduction_generale > 0 && (
                <p className="text-xs text-muted-foreground">Dont réduction générale des cotisations patronales : −{fmtEur(simResult.reduction_generale)}</p>
              )}

              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Famille</TableHead>
                      <TableHead className="text-right">Part salariale</TableHead>
                      <TableHead className="text-right">Part patronale</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {familles.map(([fam, v]) => (
                      <TableRow key={fam}>
                        <TableCell>{libelles[fam] || fam}</TableCell>
                        <TableCell className="text-right tabular-nums">{fmtEur(v.salarial)}</TableCell>
                        <TableCell className="text-right tabular-nums">{fmtEur(v.patronal)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="font-semibold">
                      <TableCell>Total</TableCell>
                      <TableCell className="text-right tabular-nums">{fmtEur(simResult.cotisations_salariales)}</TableCell>
                      <TableCell className="text-right tabular-nums">{fmtEur(simResult.cotisations_patronales)}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>

              {Array.isArray(simResult.lignes) && simResult.lignes.length > 0 && (
                <details className="text-sm">
                  <summary className="cursor-pointer text-muted-foreground">Détail des lignes ({simResult.lignes.length})</summary>
                  <div className="overflow-x-auto mt-2">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Cotisation</TableHead>
                          <TableHead className="text-right">Base</TableHead>
                          <TableHead className="text-right">Taux sal.</TableHead>
                          <TableHead className="text-right">Montant sal.</TableHead>
                          <TableHead className="text-right">Taux pat.</TableHead>
                          <TableHead className="text-right">Montant pat.</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {simResult.lignes.map((l, i) => (
                          <TableRow key={`${l.code}-${i}`}>
                            <TableCell>{l.libelle}</TableCell>
                            <TableCell className="text-right tabular-nums">{fmtEur(l.base)}</TableCell>
                            <TableCell className="text-right tabular-nums">{l.taux_salarial != null ? `${String(l.taux_salarial).replace('.', ',')} %` : '—'}</TableCell>
                            <TableCell className="text-right tabular-nums">{fmtEur(l.montant_salarial)}</TableCell>
                            <TableCell className="text-right tabular-nums">{l.taux_patronal != null ? `${String(l.taux_patronal).replace('.', ',')} %` : '—'}</TableCell>
                            <TableCell className="text-right tabular-nums">{fmtEur(l.montant_patronal)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </details>
              )}

              {simResult.hypotheses && (
                <details className="text-sm">
                  <summary className="cursor-pointer text-muted-foreground">Hypothèses</summary>
                  <ul className="mt-2 list-disc pl-5 space-y-1 text-xs text-muted-foreground">
                    {Object.entries(simResult.hypotheses).filter(([k]) => k !== 'notes').map(([k, v]) => (
                      <li key={k}><span className="font-medium">{k}</span> : {v === null || v === undefined ? '—' : typeof v === 'boolean' ? (v ? 'oui' : 'non') : String(v)}</li>
                    ))}
                    {(simResult.hypotheses.notes || []).map((n, i) => <li key={`n-${i}`}>{n}</li>)}
                  </ul>
                </details>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const Stat = ({ label, value, hint, strong }) => (
  <div className="rounded-md border p-3">
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className={`tabular-nums ${strong ? 'text-lg font-semibold' : 'text-base font-medium'}`}>{value}</p>
    {hint && <p className="text-xs text-muted-foreground">{hint} du brut</p>}
  </div>
);

export default ChargesConfig;
